

import { useEffect, useState, useCallback, useRef } from "react";
import { notificationService, type ConnectionState } from "./notificationService";

interface AppNotification {
  id: number;
  title: string;
  body: string;
  typeName: string;
  createdAt: string;
  isRead: boolean;
  isDismissed: boolean;
  templateName: string;
}

export const useNotifications = (options?: { lazy?: boolean; limit?: number }) => {
  const { lazy = false, limit } = options || {};
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(!lazy);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const hasLoadedOnceRef = useRef(false);

  const formatNotificationTime = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  }, []);

  const handleNewNotification = useCallback((notification: AppNotification) => {
    setNotifications(prev => [notification, ...prev]);
    setUnreadCount(prev => prev + 1);
  }, []);

  const handleCountUpdate = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  const loadNotifications = useCallback(async () => {
    if (hasLoadedOnceRef.current && lazy) return;

    setIsLoading(true);
    try {
      const [initialNotifications, initialCount] = await Promise.all([
        notificationService.getUserNotifications(),
        notificationService.getUnreadCount()
      ]);

      const raw: AppNotification[] = Array.isArray(initialNotifications) ? initialNotifications : [];
      const limitedNotifications = limit ? raw.slice(0, limit) : raw;

      setNotifications(limitedNotifications);
      setUnreadCount(initialCount ?? 0);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazy, limit]);

  const markAsRead = useCallback(async (deliveryId: number) => {
    const success = await notificationService.markAsRead(deliveryId);
    if (success) {
      setNotifications(prev =>
        prev.map(n => n.id === deliveryId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => (prev > 0 ? prev - 1 : 0));
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const success = await notificationService.markAllAsRead();
    if (success) {
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );
      setUnreadCount(0);
    }
  }, []);

  const dismissNotification = useCallback(async (deliveryId: number) => {
    const success = await notificationService.dismissNotification(deliveryId);
    if (success) {
      setNotifications(prev => prev.filter(n => n.id !== deliveryId));
    }
  }, []);

  useEffect(() => {
    notificationService.startConnection().then(() => {
      setConnectionState(notificationService.getConnectionState());
    });

    const interval = setInterval(() => {
      setConnectionState(notificationService.getConnectionState());
    }, 5000);

    if (!lazy) {
      loadNotifications();
    } else {
      // Even in lazy mode, fetch the unread count so the badge shows immediately
      notificationService.getUnreadCount().then((count) => {
        setUnreadCount(count);
      }).catch(() => {});
    }

    notificationService.subscribeToNotifications(handleNewNotification);
    notificationService.subscribeToUnreadCount(handleCountUpdate);

    return () => {
      clearInterval(interval);
      notificationService.unsubscribeFromNotifications(handleNewNotification);
      notificationService.unsubscribeFromUnreadCount(handleCountUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleNewNotification, handleCountUpdate, lazy]);

  return {
    notifications,
    unreadCount,
    isLoading,
    connectionState,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    formatNotificationTime,
    loadNotifications
  };
};